/**
 * The consent record every gate reads, and the verdicts drawn from it.
 *
 * Three categories: **necessary** (always on — theme, device choice, this
 * record), **analytics** (PostHog, session replay, `analytics_storage`) and
 * **marketing** (the `ad_*` Consent Mode signals and the Google Ads
 * conversions). The two optional ones are chosen independently and stored with
 * the `CONSENT_VERSION` they were chosen against, so changed copy or categories
 * can bump the version and ask again.
 *
 * `src/lib/consentRegions.ts` owns where the visitor is and what that means for
 * an undecided visitor; this module owns what the visitor decided and what that
 * does to Google and PostHog.
 */

import type { RegionRule } from "@/engine/support/consentRegions";
import {
  CONSENT_COOKIE,
  consentCookieAssignment,
  parseConsentCookie,
  type ConsentCookie,
} from "@/engine/support/consentCookie";

declare global {
  interface Window {
    /** The Google tag's global, present only once gtag.js has loaded. */
    gtag?: (...args: unknown[]) => void;
  }
}

export type OptionalCategory = "analytics" | "marketing";
export type ConsentDecision = "granted" | "denied";
export type ConsentDecisions = Record<OptionalCategory, ConsentDecision>;

export interface ConsentRecord {
  version: number;
  analytics: ConsentDecision;
  marketing: ConsentDecision;
  /** ISO timestamp of the decision. Informational. */
  decidedAt: string;
}

/** Bump when a category or materially the copy changes — re-prompts everyone. */
export const CONSENT_VERSION = 1;

export const CONSENT_PREFS_KEY = "consent_prefs";
/** The binary key this record replaces; read once for migration, then dropped. */
export const LEGACY_CONSENT_KEY = "cookie_consent";

export const OPTIONAL_CATEGORIES: readonly OptionalCategory[] = [
  "analytics",
  "marketing",
];

/**
 * Every category the surface lists, necessary first. The words themselves are
 * in the catalogue under `consent.category.<name>` — no layer here renders, and
 * a string a visitor reads has to be translatable.
 */
export const CONSENT_CATEGORIES = [
  "necessary",
  ...OPTIONAL_CATEGORIES,
] as const;

function isDecision(value: unknown): value is ConsentDecision {
  return value === "granted" || value === "denied";
}

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function parseRecord(raw: string | null): ConsentRecord | null {
  if (!raw) return null;
  try {
    const parsed: Record<string, unknown> = JSON.parse(raw);
    if (
      typeof parsed.version === "number" &&
      isDecision(parsed.analytics) &&
      isDecision(parsed.marketing)
    ) {
      return {
        version: parsed.version,
        analytics: parsed.analytics,
        marketing: parsed.marketing,
        decidedAt: typeof parsed.decidedAt === "string" ? parsed.decidedAt : "",
      };
    }
  } catch {
    /* a corrupt value is no choice */
  }
  return null;
}

function writeRecord(record: ConsentRecord): void {
  try {
    localStorage.setItem(CONSENT_PREFS_KEY, JSON.stringify(record));
  } catch {
    /* a browser refusing storage still gets the in-page decision */
  }
  mirrorToCookie(record);
}

/**
 * Mirrors the decision where the server can read it, so the next document is
 * rendered already knowing whether to draw the surface (#531).
 *
 * Writing it is best effort in the same way storing the record is: a browser
 * that refuses still gets the in-page decision, and the server simply falls
 * back to asking.
 */
function mirrorToCookie(record: ConsentRecord): void {
  // The server writes no cookies here; it only ever reads the one a browser
  // left behind.
  if (typeof document === "undefined") return;
  const value: ConsentCookie = {
    version: record.version,
    analytics: record.analytics,
    marketing: record.marketing,
  };
  try {
    document.cookie = consentCookieAssignment(
      value,
      window.location.protocol === "https:",
    );
  } catch {
    /* a browser refusing cookies still gets the in-page decision */
  }
}

/**
 * The stored decision, or `null` for "no choice yet". A leftover
 * `"granted"`/`"denied"` from the previous binary mechanism is migrated in
 * place: it maps to both optional categories. Anything uninterpretable is no
 * choice.
 */
export function readConsent(): ConsentRecord | null {
  const record = parseRecord(readStorage(CONSENT_PREFS_KEY));
  if (record) return record;

  const legacy = readStorage(LEGACY_CONSENT_KEY);
  if (legacy === "granted" || legacy === "denied") {
    const migrated: ConsentRecord = {
      version: CONSENT_VERSION,
      analytics: legacy,
      marketing: legacy,
      decidedAt: new Date().toISOString(),
    };
    writeRecord(migrated);
    return migrated;
  }

  // A decision made before the application was live arrives as the cookie
  // alone: the surface's form posts to the server, which can write nothing
  // else. Adopted into the store, it is a decision like any other.
  const cookie = parseConsentCookie(readCookie(CONSENT_COOKIE));
  if (cookie) {
    const adopted: ConsentRecord = { ...cookie, decidedAt: "" };
    writeRecord(adopted);
    return adopted;
  }
  return null;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  const entry = document.cookie
    .split("; ")
    .find((part) => part.startsWith(prefix));
  return entry ? entry.slice(prefix.length) : null;
}

/** The stored decision only if it is current; a stale version reads as none. */
export function activeConsent(): ConsentRecord | null {
  const record = readConsent();
  return record && record.version >= CONSENT_VERSION ? record : null;
}

/** Whether the surface must be presented: no record, or an out-of-date one. */
export function needsConsentChoice(
  record: ConsentRecord | null,
  version = CONSENT_VERSION,
): boolean {
  return !record || record.version < version;
}

/** The decision in force for each optional category: the record, else region default. */
export function effectiveConsent(
  record: ConsentRecord | null,
  rule: RegionRule,
): ConsentDecisions {
  if (record && record.version >= CONSENT_VERSION) {
    return { analytics: record.analytics, marketing: record.marketing };
  }
  return { ...rule.defaults };
}

/** Whether Connect must wait for a choice: needed *and* the region requires one. */
export function blocksConnect(
  record: ConsentRecord | null,
  rule: RegionRule,
  version = CONSENT_VERSION,
): boolean {
  return needsConsentChoice(record, version) && rule.requiresChoice;
}

function gtag(): Window["gtag"] {
  if (typeof window === "undefined") return undefined;
  return window.gtag;
}

/**
 * Whether the module that owns PostHog has been asked for yet. A denial before
 * anything was started has nothing to switch off, and asking for the module in
 * order to tell it so would be fetching PostHog in order not to run it.
 */
let analyticsModuleWanted = false;

/** The most recent hand-over, so a caller can wait for it. */
let analyticsHandover: Promise<void> = Promise.resolve();

/**
 * Hands the analytics decision to the module that acts on it, fetching that
 * module the first time a decision calls for it.
 *
 * The dependency runs this way round on purpose. PostHog is the heaviest thing
 * in the bundle and the entry screen draws none of it, so a static import here
 * would put it in front of the first paint of every page — this module is read
 * at load to apply a stored choice (#531). A dynamic import leaves it a chunk
 * of its own, fetched when a visitor has said yes.
 */
function handAnalyticsDecisionOver(granted: boolean): void {
  if (!granted && !analyticsModuleWanted) return;
  analyticsModuleWanted = true;
  analyticsHandover = import("@/engine/diagnostics/analytics")
    .then((analytics) => {
      analytics.applyAnalyticsConsent(granted);
    })
    .catch(() => {
      // Telemetry that could not be fetched is telemetry that does not run,
      // which is the same outcome as a blocked tag and needs no handling. It is
      // caught rather than left to reject because the boot watchdog reloads the
      // document on an unhandled rejection that reads like a chunk failure.
    });
}

/**
 * Settles once the standing decision has reached the analytics module, or at
 * once where no decision has called for it yet.
 *
 * The hand-over above is deliberately not awaited by the caller — a visitor
 * choosing "yes" should not wait on a network fetch to be told Google has been
 * updated. Something has to be able to wait for it all the same: a test that
 * asserts on what PostHog was told, and anything that must not tear down its
 * world while a fetch for this module is still in the air.
 */
export function analyticsDecisionDelivered(): Promise<void> {
  return analyticsHandover;
}

/**
 * Pushes a set of decisions into Google Consent Mode and PostHog. Marketing
 * drives the three `ad_*` signals; analytics drives `analytics_storage` and
 * whether PostHog runs at all.
 *
 * Google is told synchronously, because the tag may already be on the page and
 * reads the dataLayer as it goes. PostHog is told as soon as its module lands.
 */
export function applyConsent(decisions: ConsentDecisions): void {
  gtag()?.("consent", "update", {
    ad_storage: decisions.marketing,
    ad_user_data: decisions.marketing,
    ad_personalization: decisions.marketing,
    analytics_storage: decisions.analytics,
  });
  handAnalyticsDecisionOver(decisions.analytics === "granted");
}

/** Stores a fresh choice at the current version and applies it immediately. */
export function saveConsent(decisions: ConsentDecisions): ConsentRecord {
  const record: ConsentRecord = {
    version: CONSENT_VERSION,
    analytics: decisions.analytics,
    marketing: decisions.marketing,
    decidedAt: new Date().toISOString(),
  };
  writeRecord(record);
  try {
    localStorage.removeItem(LEGACY_CONSENT_KEY);
  } catch {
    /* nothing to clean up if storage is unavailable */
  }
  applyConsent(decisions);
  return record;
}

/**
 * Applies a stored, current choice at page load. An undecided visitor is left
 * alone here on purpose: the consent surface resolves the region and applies
 * its default, so PostHog never initialises for a visitor whose region denies
 * analytics.
 */
export function applyStoredConsent(): void {
  const record = activeConsent();
  if (record) {
    // A visitor who decided before the cookie existed has their decision only
    // in storage, and the server would draw the surface at them again. Mirror
    // it on the way past, so this is the last document that has to ask.
    mirrorToCookie(record);
    applyConsent({ analytics: record.analytics, marketing: record.marketing });
  }
}
